/**
 * Service API pour la gestion des prises de besoin (nouvelle table besoins)
 */

import { readApiJson } from '../utils/readApiJson';

const API_BASE_URL = '/api/prisme';

export interface BesoinData {
  id?: number;
  client_id: number;
  nom?: string;
  description?: string;
  template_id: string;
  template_version: string;
  phase1_data?: Record<string, any>;
  phase2_data?: Record<string, any>;
  phase3_data?: Record<string, any>;
  phase4_data?: Record<string, any>;
  phase5_data?: Record<string, any>;
  phase6_data?: Record<string, any>;
  phase7_data?: Record<string, any>;
  phase8_data?: Record<string, any>;
  phase9_data?: Record<string, any>;
  phase10_data?: Record<string, any>;
  phase5_metier_data?: Record<string, any>;
  metier_id?: string;
  statut?: 'brouillon' | 'en_cours' | 'complet' | 'approuve_prisme' | 'approuve_client' | 'archivé';
  metadata?: {
    completed_phases?: string[];
    completion_percentage?: number;
    last_modified_phase?: string;
  };
  showpriseclient?: boolean;
  makeby?: string | null;
  archived?: boolean;
  approuve_prisme?: boolean;
  approuve_prisme_date?: string;
  approuve_prisme_par?: number;
  approuve_client?: boolean;
  approuve_client_date?: string;
  approuve_client_par?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  last_modified_by?: number;
}

export async function getAllBesoins(clientId?: number): Promise<BesoinData[]> {
  try {
    const url = clientId ? `${API_BASE_URL}/besoins?clientId=${clientId}` : `${API_BASE_URL}/besoins`;
    const response = await fetch(url, { credentials: 'include' });
    const data = await readApiJson<BesoinData[] | { error?: string }>(response);
    if (!response.ok) {
      throw new Error((data as { error?: string })?.error || 'Erreur lors de la récupération des prises de besoin');
    }
    return data as BesoinData[];
  } catch (error) {
    console.error('Erreur getAllBesoins:', error);
    throw error;
  }
}

export async function getBesoinsByClient(clientId: number): Promise<BesoinData[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/besoins/client/${clientId}`, { credentials: 'include' });
    const data = await readApiJson<BesoinData[] | { error?: string }>(response);
    if (!response.ok) {
      throw new Error((data as { error?: string })?.error || 'Erreur lors de la récupération des prises de besoin');
    }
    return data as BesoinData[];
  } catch (error) {
    console.error('Erreur getBesoinsByClient:', error);
    throw error;
  }
}

export async function getBesoinById(besoinId: number): Promise<BesoinData | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/besoin/${besoinId}`, { credentials: 'include' });
    const data = await readApiJson<BesoinData | { error?: string }>(response);
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error((data as { error?: string })?.error || 'Erreur lors de la récupération de la prise de besoin');
    }
    return data as BesoinData;
  } catch (error) {
    console.error('Erreur getBesoinById:', error);
    throw error;
  }
}

export async function getClient2026TestResponses(): Promise<Record<string, string | string[]>> {
  const response = await fetch(`${API_BASE_URL}/client2026/test-responses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      templateId: 'prise-de-besoin-client-2026',
      templateVersion: '3.1.0',
      schema: 3,
    }),
  });
  const result = await readApiJson<{
    responses?: Record<string, string | string[]>;
    error?: string;
  }>(response);
  if (!response.ok || !result?.responses) {
    throw new Error(result?.error || 'Impossible de générer les réponses tests');
  }
  return result.responses;
}

export async function getOrCreateClient2026AsWasBaseline(
  besoinId: number,
): Promise<Record<string, number>> {
  const response = await fetch(`${API_BASE_URL}/besoin/${besoinId}/client2026-as-was-baseline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  });
  const result = await readApiJson<{ baseline?: Record<string, number>; error?: string }>(response);
  if (!response.ok || !result?.baseline) {
    throw new Error(result?.error || 'Impossible de figer la référence Passé');
  }
  return result.baseline;
}

export async function createBesoin(data: Partial<BesoinData>): Promise<BesoinData> {
  try {
    const response = await fetch(`${API_BASE_URL}/besoin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    const result = await readApiJson<any>(response);
    if (!response.ok) {
      throw new Error(result?.error || 'Erreur lors de la création de la prise de besoin');
    }
    return result.data || result;
  } catch (error) {
    console.error('Erreur createBesoin:', error);
    throw error;
  }
}

export async function updateBesoin(
  besoinId: number,
  data: Partial<BesoinData>,
  modifiedBy?: number,
  commentaire?: string
): Promise<BesoinData> {
  try {
    const response = await fetch(`${API_BASE_URL}/besoin/${besoinId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...data, modified_by: modifiedBy, commentaire }),
    });

    const result = await readApiJson<any>(response);
    if (!response.ok) {
      throw new Error(result?.error || 'Erreur lors de la mise à jour de la prise de besoin');
    }
    return result.data || result;
  } catch (error) {
    console.error('Erreur updateBesoin:', error);
    throw error;
  }
}

export async function deleteBesoin(besoinId: number, deletedBy?: number): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/besoin/${besoinId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ deleted_by: deletedBy }),
    });

    const errBody = await readApiJson<{ error?: string } | undefined>(response);
    if (!response.ok) {
      throw new Error(errBody?.error || 'Erreur lors de la suppression de la prise de besoin');
    }
  } catch (error) {
    console.error('Erreur deleteBesoin:', error);
    throw error;
  }
}

export async function getBesoinHistorique(besoinId: number): Promise<any[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/besoin/${besoinId}/historique`, { credentials: 'include' });
    const data = await readApiJson<any[] | { error?: string }>(response);
    if (!response.ok) {
      throw new Error((data as { error?: string })?.error || "Erreur lors de la récupération de l'historique");
    }
    return data as any[];
  } catch (error) {
    console.error('Erreur getBesoinHistorique:', error);
    throw error;
  }
}
